import React, { Fragment, useContext, useMemo, useRef, useState } from 'react';
import classes from './InstagramPostTypeBlock.module.scss';
import { UseFormReturn } from 'react-hook-form';
import { RadioButtonsType } from '../../../components/forms/RadioButtons/RadioButtons.view';
import { MediaType, PostFeedType, PostPublishMethod } from '../../../models/graphql';
import { CSSTransition } from 'react-transition-group';
import { RadioButtons } from '../../../components/forms/RadioButtons';
import { FormElementAction } from '../../../models/basic-objects';
import { useDidMount } from '../../../hooks/useDidMount';
import classNames from 'classnames';
import { Tooltip } from '../../../components/actions/Tooltip';
import { GroupPostAttributesContentType } from '../../../models/post-editor';
import { ValidationMessages } from '../../../constants/validation-messages';
import { BoxNotification } from '../../../components/containers/BoxNotification';
import { IgProfileType } from './InstagramPostTypeBlock.controller';
import { getInfoBoxErrors } from '../helpers/instagramPublishingRules/instagramPublishingRules';
import { MessageInfo } from '../helpers/instagramPublishingRules/messages';
import { PostEditorContext } from '../states/PostEditorProvider';
import { ActiveCustomizeIndexContext } from '../states/ActiveCustomizeIndexProvider';
import { PostEditorGroupDataContext } from '../states/PostEditorGroupDataProvider';
import { getTextLength } from '../TextEditor/CharacterCounter/CharacterCounter.helpers';
import { PostData } from '../helpers/instagramPublishingRules/types';
import { useMobileApps } from '../../../hooks/useMobileApps';
import { BeaconContext } from '../../../providers/BeaconProvider';
import { idGenerator } from 'helpers/idGenerator';
import scssVars from '../../../assets/scss/exportedVars.module.scss';

export type InstagramPostTypeBlockViewProps = {
  formInstance: UseFormReturn;
  updateIgTypeAction: FormElementAction;
  updateIgPublishMethodAction: FormElementAction;
  igType: PostFeedType;
  igPublishMethod: GroupPostAttributesContentType['publishMethod'];
  igTypeName: string;
  igPublishMethodName: string;
  igProfilesType?: IgProfileType;
};

export const InstagramPostTypeBlockView = (props: InstagramPostTypeBlockViewProps): JSX.Element => {
  const {
    formInstance,
    updateIgPublishMethodAction,
    updateIgTypeAction,
    igType,
    igPublishMethod,
    igTypeName,
    igPublishMethodName,
    igProfilesType
  } = props;
  const { connectedApps } = useMobileApps();
  const appInstalled = connectedApps.length > 0;
  const [mount, setMount] = useState<boolean>(false);
  const cssRef = useRef<HTMLDivElement>(null);
  const { profiles, state } = useContext(PostEditorContext);
  const customizeIndex = useContext(ActiveCustomizeIndexContext);
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const { postGroupIndex } = tabsState;
  const { beacon } = useContext(BeaconContext);
  const { postGroups } = state;
  const postGroup = postGroups[postGroupIndex];
  const { customizeContentPerProfile, profiles: selectedProfiles } = postGroup;
  const activeCustomIndex =
    typeof customizeIndex === 'number' && customizeContentPerProfile ? customizeIndex : undefined;
  const { content } =
    typeof activeCustomIndex === 'number'
      ? state.postGroups[postGroupIndex].posts[activeCustomIndex]
      : state.postGroups[postGroupIndex].groupPostAttributes;
  const postMedia = content.media;
  const hasMultipleMedia =
    postMedia.filter(media => media.type === MediaType.PHOTO || media.type === MediaType.VIDEO).length >= 2;
  const isOnlyImages = postMedia.every(media => media.type === MediaType.PHOTO);
  const textLength = content.texts.reduce(
    (currentTextLength, paragraph) => currentTextLength + getTextLength(paragraph.text),
    0
  );
  const directPublishProfiles = selectedProfiles.filter(profile => {
    const matchedProfile = profiles.find(p => p.id === profile.profileId);
    return (
      matchedProfile && matchedProfile.__typename === 'InstagramSocialProfile' && matchedProfile.directPublishEnabled
    );
  });

  const hasDirectPublishProfile = directPublishProfiles.length >= 1;
  const hasProfileWithoutDirectPublish = directPublishProfiles.length !== selectedProfiles.length;
  const instaProfileDataForRules: PostData = {
    profileType: igProfilesType,
    publishingMethod: igPublishMethod,
    feedType: igType === PostFeedType.STORY ? 'story' : 'feed/reel',
    hasMobile: appInstalled,
    hasDirectPublishProfile,
    hasProfileWithoutDirectPublish,
    hasMultipleMedia,
    hasPostText: textLength >= 1,
    media: {
      type: postMedia[0]?.type,
      filesize: postMedia[0]?.filesize,
      width: postMedia[0]?.width,
      height: postMedia[0]?.height,
      videoLength: postMedia[0]?.length
    }
  };
  const infoMessages = getInfoBoxErrors(
    [
      'creatorStoryManual',
      'videoUnder3Sec',
      'creatorStoryManual',
      'directVideoOver15Mins',
      'directVideoOver15MinsWide',
      'directVideoOver15MinsTall',
      'forceReminder',
      'manualVideoOver15MinsA',
      'manualVideoOver15MinsB',
      'videoStoryOver60Sec',
      'directStoryNoCaptions',
      'cropDirectImageStoryWide',
      'cropDirectImageStoryTall',
      'cropDirectImageStoryMidTall',
      'cropDirectVideoStoryWide',
      'cropDirectVideoStoryTall',
      'cropDirectVideoStoryMidTall',
      'manualStoryA',
      'manualStoryB',
      'tallFeedVideo',
      'wideManualFeedVideoA',
      'wideManualFeedVideoB',
      'MultiImageStoryManual',
      'MultiImageFeedCrop',
      'EnableDirectPublishNoMobile',
      'EnableDirectPublishHaveMobile',
      'DirectAndMobileReminder',
      'DirectAndEmailReminder'
    ],
    instaProfileDataForRules
  ).map((info, index) => (
    <Fragment key={`message_${info}_${index}`}>
      <MessageInfo key={info} instaProfilePostData={instaProfileDataForRules} hasMobile={appInstalled} />
    </Fragment>
  ));

  const igTypeItems: RadioButtonsType[] = [
    {
      label: hasMultipleMedia && isOnlyImages ? 'Feed' : 'Feed/Reel',
      value: PostFeedType.FEED
    },
    {
      label: 'Story',
      value: PostFeedType.STORY
    }
  ];

  const igPublishMethodItems: RadioButtonsType[] = [
    {
      label: 'Attempt Direct Publishing',
      value: PostPublishMethod.DIRECT
    },
    {
      label: 'Receive a Notification',
      value: PostPublishMethod.MANUAL
    }
  ];

  useDidMount(() => {
    setMount(true);
    return () => {
      setMount(false);
    };
  });

  const tooltipId = useMemo(() => idGenerator('tooltipPostType'), []);
  return (
    <CSSTransition in={mount} timeout={300} classNames={'instagram-block'} nodeRef={cssRef} unmountOnExit>
      <div className={classes.InstagramPostTypeBlock} ref={cssRef}>
        <div className={'grid-x align-middle margin-bottom-small-16'}>
          <div className={classNames(classes.InstagramPostTypeLabel, 'cell shrink')}>Instagram post type</div>
          <div className={'cell shrink'}>
            <Tooltip
              target={`#tooltipPostType${tooltipId}`}
              type={'inlined'}
              autoHide={false}
              autoZIndex={false}
              style={{ zIndex: scssVars['z-index-overmodal'] }}>
              <div>
                <p className={'small-text'}>Videos published to your Feed will also appear as a Reel.</p>
                <p className={'no-b-m small-text'}>
                  <a onClick={() => beacon('article', '721', { type: 'modal' })}>Learn more about publishing Instagram Stories</a>
                </p>
              </div>
            </Tooltip>
          </div>
        </div>
        <RadioButtons
          action={updateIgTypeAction}
          value={igType ?? PostFeedType.FEED}
          wrapper={true}
          displayType={'inline'}
          formInstance={formInstance}
          items={igTypeItems}
          name={igTypeName}
          rules={{}}
          size={'small'}
        />
        <div className={'grid-x align-middle margin-bottom-small-16'}>
          <div className={classNames(classes.InstagramPostTypeLabel, 'cell', 'shrink')}>
            Instagram Publishing Method
          </div>
          <div className={'cell shrink'}>
            <Tooltip
              target={`#tooltipTarget${tooltipId}`}
              position={'right'}
              pointer
              type={'inlined'}
              autoHide={false}
              autoZIndex={false}
              style={{ zIndex: scssVars['z-index-overmodal'] }}>
              <div>
                <p className={'small-text'}>
                  Some Instagram features — like filters, locations, and tags — aren’t available with direct publishing.
                  Choose “Receive a Notification” to manually add these before posting.
                </p>
                <p className={'no-b-m small-text'}>
                  <a onClick={() => beacon('article', '721', { type: 'modal' })}>Learn more about direct publishing and notifications</a>
                </p>
              </div>
            </Tooltip>
          </div>
        </div>
        <RadioButtons
          action={updateIgPublishMethodAction}
          {...(igPublishMethod ? { value: igPublishMethod } : {})}
          wrapper={false}
          displayType={'inline'}
          formInstance={formInstance}
          items={igPublishMethodItems}
          name={igPublishMethodName}
          rules={{
            required: ValidationMessages.select.required
          }}
          size={'small'}
        />
        {/* TODO WE ADDED false to this check in order to temporary disable IG messages 05/02/2025
           https://app.asana.com/0/1191520030670857/1209101958536125 */}
        {infoMessages.length >= 1 && false ? (
          <div className={classNames('margin-top-small-16', classes.BoxNotification)}>
            <BoxNotification type={'info'}>{infoMessages}</BoxNotification>
          </div>
        ) : null}
      </div>
    </CSSTransition>
  );
};
